/**
 * THE EXAM EDITOR'S SERVER SEAM — the mutation a client-side editor calls with new typst source
 * (`compileExamMutation`), and the read-only query it calls to render a preview without saving anything
 * (`getExamSvgQuery`).
 *
 * A THIN SHELL AROUND `compileAndPersistExam`/`compileExamSource`. Editing already goes through
 * exam-document.ts entirely on the client (encode, patch a node, decode back to source) — by the time
 * source reaches here it is already a complete document, so `compileExamMutation`'s only job is: compile
 * it, persist it if it is valid, and hand back a result the client can render without needing to know the
 * difference between a thrown TypstCompileError, an ExamStructureError and an unrelated failure.
 *
 * THE RESULT IS A JSON STRING, not a typed entity. `ExamEditorSaveResult`/`ExamSvgResult` are plain shapes
 * with no fields a teacher's data depends on — same convention used elsewhere in this codebase for a result
 * that only a client UI reads (see e.g. `updatedBoxesJson` in rerun-boundingbox-ocr.ts) — so it costs
 * nothing to change without a BaseQL entity migration.
 *
 * SERVER ONLY from the dynamic import down: see compile-exam.ts's own note on why — this file itself has no
 * node-only imports, so it is safe for the client bundle to import the result types as types.
 */

import { logger } from '@adornis/base/logging.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import type { ExamLayout } from './exam-structure.js';

export interface ExamEditorProblem {
  severity: 'error' | 'warning';
  message: string;
  taskNumber?: string;
}

export interface ExamEditorSaveResult {
  ok: boolean;
  problems?: ExamEditorProblem[];
  message?: string;
  svg?: string;
  layout?: ExamLayout;
  /**
   * Only set by `requestExamRefinementMutation`: the source it wrote is not what the client sent (a job
   * wrote it server-side), so the caller needs it back to re-sync its own document store — unlike
   * `compileExamMutation`, whose caller already holds the source it just saved.
   */
  source?: string;
}

export interface ExamSvgResult {
  ok: boolean;
  svg?: string;
  /**
   * Recomputed together with `svg`, from the same compile — a stale `Exam.examLayoutJson` (last written by
   * an actual save, not this read-only preview) can fall out of sync with this fresh render the moment the
   * house template's own layout changes, even though the exam's own source hasn't: the boxes below would
   * otherwise anchor hitboxes to a document that no longer matches what's on screen. See ExamEditor's own
   * `layout` state, which now takes this over `exam.examLayoutJson` whenever a preview arrives.
   */
  layout?: ExamLayout;
  message?: string;
}

/**
 * The same message on every locked refusal in this file, worded for the banner `ExamEditorSaveResult.message`
 * already renders (`Canvas.examEditor.notSaved`) — plain English, same convention as every other message
 * here (`the change could not be applied`, `This is taking longer than expected …`), left untranslated at
 * this layer like those.
 */
const EXAM_LOCKED_MESSAGE =
  'This exam is locked — a master solution, grading scheme, or student submissions already exist, so it can no longer be edited here.';

export const compileExamMutation = registerMutation({
  operationName: 'compileExamMutation',
  type: () => String,
  params: [
    { name: 'examId', type: () => String },
    { name: 'source', type: () => String },
  ],
  resolve: (examId: string, source: string) => async () => {
    // Checked FIRST, before any compile work: a manual hitbox/AI edit is the one entry point that bypasses
    // the `examCreation` job entirely (see compile-exam.ts's own note on this function rewriting the tree
    // wholesale), so it is also the one place `isExamCreatorLocked` has to be asked directly rather than
    // relying on the job's own `ready()` — see exam-lock.ts for why every enforcement point shares this
    // one definition.
    const { resolveIsExamCreatorLocked } = await import('../../utils/exam-lock.js');
    if (await resolveIsExamCreatorLocked(examId)) {
      const saveResult: ExamEditorSaveResult = { ok: false, message: EXAM_LOCKED_MESSAGE };
      return JSON.stringify(saveResult);
    }

    const { compileAndPersistExam, ExamStructureError } = await import('./compile-exam.js');

    try {
      const { assets, figureAssets, plotAssets } = await loadPersistedAssets(examId);
      const result = await compileAndPersistExam(examId, source, assets, figureAssets, plotAssets);
      const warnings = result.problems.filter(problem => problem.severity === 'warning');
      const saveResult: ExamEditorSaveResult = {
        ok: true,
        problems: warnings.length > 0 ? warnings : undefined,
        svg: result.svg,
        layout: result.layout,
      };
      return JSON.stringify(saveResult);
    } catch (err) {
      if (err instanceof ExamStructureError) {
        const saveResult: ExamEditorSaveResult = { ok: false, problems: [...err.errors, ...err.warnings] };
        return JSON.stringify(saveResult);
      }
      logger.error({ err, examId }, 'Exam editor save failed');
      const saveResult: ExamEditorSaveResult = { ok: false, message: err instanceof Error ? err.message : String(err) };
      return JSON.stringify(saveResult);
    }
  },
});

/**
 * THE EDITOR'S "TELL AI WHAT TO CHANGE" BOX — one exam-creation job, dispatched with `refinementInstruction`
 * exactly the way `create-exam.tool.ts`'s "anpassungsanweisung" is (see `exam-creation.job.ts`), except the
 * caller here is a teacher waiting on a popover instead of a chat turn that must not block. `dispatchJobsAndWait`
 * is what makes that difference free: the job type itself declares no `waitMs`, so it falls back to the type's
 * own full runtime budget — plenty for a wait a teacher is actively watching a spinner for.
 *
 * RETURNS THE SAME SHAPE `compileExamMutation` DOES, on purpose: the editor already knows how to take an
 * `ExamEditorSaveResult` and redraw itself from `svg`/`layout`, and this is exactly that redraw, just triggered
 * by a finished job instead of a manual edit. The job itself only persists source/tasks/assets — it has no
 * reason to also hand back a render — so once it reports done this re-derives the preview the same way
 * `getExamSvgQuery` does, from whatever it just wrote.
 */
export const requestExamRefinementMutation = registerMutation({
  operationName: 'requestExamRefinementMutation',
  type: () => String,
  params: [
    { name: 'examId', type: () => String },
    { name: 'instruction', type: () => String },
  ],
  resolve: (examId: string, instruction: string) => async () => {
    // Same check as `compileExamMutation`'s, and for the same reason it is asked explicitly rather than
    // left to the `examCreation` job's own `ready()` drop: a dropped-before-run job may report nothing
    // clearer than a generic failure, and the teacher waiting on this popover deserves the real reason.
    const { resolveIsExamCreatorLocked } = await import('../../utils/exam-lock.js');
    if (await resolveIsExamCreatorLocked(examId)) {
      const saveResult: ExamEditorSaveResult = { ok: false, message: EXAM_LOCKED_MESSAGE };
      return JSON.stringify(saveResult);
    }

    const { dispatchJobsAndWait } = await import('../jobs/dispatch.js');

    const { outcome, jobIds } = await dispatchJobsAndWait('examCreation', examId, {
      refinementInstruction: instruction,
    });

    if (outcome !== 'done') {
      if (outcome === 'pending') {
        const saveResult: ExamEditorSaveResult = {
          ok: false,
          message: 'This is taking longer than expected — the change is still being applied in the background.',
        };
        return JSON.stringify(saveResult);
      }
      const { getJob } = await import('../jobs/job-store.js');
      const failedJob = jobIds[0] ? await getJob(jobIds[0]) : null;
      const saveResult: ExamEditorSaveResult = {
        ok: false,
        message: failedJob?.lastError ?? 'the change could not be applied',
      };
      return JSON.stringify(saveResult);
    }

    try {
      const { compileExamSource } = await import('./compile-exam.js');
      const { getCollection } = await import('@adornis/baseql/server/collections.js');
      const { Exam } = await import('../../exam.js');

      const examCollection = await getCollection(Exam);
      const exam = await examCollection.findOne({ _id: examId });
      const source = String((exam as any)?.examSource ?? '');
      const { assets } = await loadPersistedAssets(examId);
      const result = await compileExamSource(source, assets);
      const saveResult: ExamEditorSaveResult = { ok: true, svg: result.svg, layout: result.layout, source };
      return JSON.stringify(saveResult);
    } catch (err) {
      logger.error({ err, examId }, 'Exam refinement preview render failed');
      const saveResult: ExamEditorSaveResult = { ok: false, message: err instanceof Error ? err.message : String(err) };
      return JSON.stringify(saveResult);
    }
  },
});

/**
 * Renders the editor's preview SVG on demand, without persisting it — see the note on `Exam` about why the
 * SVG cannot live on the entity once a figure is involved. Recompiling here costs about ten milliseconds
 * (see compile-exam.ts), which is cheap next to a document write that risks losing everything else in it.
 */
export const getExamSvgQuery = registerQuery({
  operationName: 'getExamSvgQuery',
  type: () => String,
  params: [{ name: 'examId', type: () => String }],
  resolve: (examId: string) => async (): Promise<string> => {
    const { compileExamSource } = await import('./compile-exam.js');
    const { getCollection } = await import('@adornis/baseql/server/collections.js');
    const { Exam } = await import('../../exam.js');

    try {
      const examCollection = await getCollection(Exam);
      const exam = await examCollection.findOne({ _id: examId });
      const source = String((exam as any)?.examSource ?? '');
      if (!source) return JSON.stringify({ ok: false, message: 'exam has no source yet' } satisfies ExamSvgResult);

      const { assets } = await loadPersistedAssets(examId);
      const result = await compileExamSource(source, assets);
      return JSON.stringify({ ok: true, svg: result.svg, layout: result.layout } satisfies ExamSvgResult);
    } catch (err) {
      logger.error({ err, examId }, 'Exam preview render failed');
      const message = err instanceof Error ? err.message : String(err);
      return JSON.stringify({ ok: false, message } satisfies ExamSvgResult);
    }
  },
});

/**
 * Rehydrates the exam's already-persisted figures AND plots for a recompile. The editor only ever has the
 * typst SOURCE in hand — not the image bytes a fresh creation job still holds in memory, and not a
 * previous compile's plot SVG either (see `Exam.plotAssets`'s doc comment: that SVG was never durable to
 * begin with) — so without this, a recompile from here can neither resolve `/assets/<materialId>.<ext>`
 * (typst fails to find the file) nor keep `Exam.figureAssets`/`plotAssets` populated
 * (compileAndPersistExam rewrites both wholesale from what it is given).
 *
 * A PLOT'S SVG IS REGENERATED, NOT STORED — its persisted form is the typst source that produced it (see
 * exam-plots.ts), recompiled here the same way `compileStandaloneSvg` did the first time. A plot whose
 * stored code no longer compiles (a pinned package going missing, say) is skipped with a warning rather
 * than failing the whole recompile — the same "not found" typst error the caller would have hit anyway,
 * but scoped to that one figure instead of blocking every other edit on the exam.
 */
export async function loadPersistedAssets(examId: string): Promise<{
  assets: { name: string; bytes: Buffer }[];
  figureAssets: { materialId: string; fileId: string }[];
  plotAssets: { materialId: string; code: string }[];
}> {
  const { getCollection } = await import('@adornis/baseql/server/collections.js');
  const { getByID } = await import('@adornis/baseql/operations/mongo.js');
  const { ImageFile } = await import('@adornis/file-utils/db/images.js');
  const { readFile } = await import('node:fs/promises');
  const { Exam } = await import('../../exam.js');
  const { compileStandaloneSvg } = await import('./compile-exam.js');

  const examCollection = await getCollection(Exam);
  const exam = await examCollection.findOne({ _id: examId });
  const figureAssets: { materialId: string; fileId: string }[] = Array.isArray((exam as any)?.figureAssets)
    ? (exam as any).figureAssets
    : [];
  const plotAssets: { materialId: string; code: string }[] = Array.isArray((exam as any)?.plotAssets)
    ? (exam as any).plotAssets
    : [];

  const assets: { name: string; bytes: Buffer }[] = [];
  for (const { materialId, fileId } of figureAssets) {
    // eslint-disable-next-line no-await-in-loop -- sequential reads of a handful of already-persisted figures
    const imageFile = await getByID(ImageFile, fileId)({ meta: { extension: 1 } } as any);
    const extension = String((imageFile as any)?.meta?.extension ?? '');
    if (!imageFile || !extension) continue;
    // eslint-disable-next-line no-await-in-loop -- see above
    const bytes = await readFile(imageFile.getFullPath());
    assets.push({ name: `${materialId}.${extension}`, bytes: Buffer.from(bytes) });
  }
  for (const { materialId, code } of plotAssets) {
    try {
      // eslint-disable-next-line no-await-in-loop -- sequential recompiles of a handful of already-verified plots
      const svg = await compileStandaloneSvg(code);
      assets.push({ name: `${materialId}.svg`, bytes: Buffer.from(svg, 'utf8') });
    } catch (err) {
      logger.error({ err, examId, materialId }, 'Stored plot code no longer compiles, skipping it on recompile');
    }
  }

  return { assets, figureAssets, plotAssets };
}
